import { useNavigate } from 'react-router';
import { ContentLayout } from '../../../Layouts/ContentLayout/ContentLayout';
import { NoResults } from '@/components/NoResults/NoResults';
import NoDepositAccountsIcon from '../../../../images/NoItemsIcon.svg?react';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { Paginator } from '@/components/Paginator/Paginator';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { DepositAccountsList } from './ui/DepositAccountsList';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { Button } from '@/components/Button/Button';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import PlusIcon from '@/images/PlusIcon.svg?react';
import { useGetSearchParams } from '@/hooks/useGetSearchParams';
import { getCreateDepositAccountPath } from '@/bundle/_OrgAdmin/pages/DepositAccountsPage/_CreateDepositAccountPage/path/path';
import { useSubscriptionStatus } from '@/bundle/shared/hooks/useSubscriptionStatus';
import { OrganizationSubscriptionStatusBanner } from '@/bundle/shared/components/banner/OrganizationSubscriptionStatusBanner/OrganizationSubscriptionStatusBanner';
import { getListData } from '@/helpers/helpers';
import { loadDepositAccounts } from '@/bundle/_OrgAdmin/pages/DepositAccountsPage/api/api';

export const DepositAccountsPage = () => {
  const navigate = useNavigate();
  const searchParams = useGetSearchParams();
  const { isInactiveSubscription, isViewModeSubscription } = useSubscriptionStatus();

  const { data, isPending } = useQuery({
    queryKey: ['load_deposit_accounts', searchParams],
    queryFn: () => loadDepositAccounts(searchParams),
    placeholderData: keepPreviousData,
  });

  const navigateToCreateAccount = () => {
    navigate(getCreateDepositAccountPath());

    amplitudeService.logEvent(AMPLITUDE_EVENTS.CreateDepositAccountStartRedirect);
  };

  const { list, totalCount, hasData, hasPagination } = getListData(data?.body, searchParams);

  return (
    <ContentLayout
      title='Receiving Accounts'
      breadcrumbs='Receiving Accounts'
      headerAction={
        <Button
          disabled={isViewModeSubscription}
          width={191}
          mobileStretch
          icon={<PlusIcon className={isViewModeSubscription ? 'disabled-svg-icon' : ''} />}
          iconPosition='left'
          onClick={navigateToCreateAccount}
        >
          Create Account
        </Button>
      }
      subtitle='Bank accounts your organization uses to receive funds.'
      banner={isInactiveSubscription && <OrganizationSubscriptionStatusBanner />}
    >
      <PageBody>
        {hasData && <DepositAccountsList list={list} />}

        {!hasData && !isPending && (
          <NoResults icon={<NoDepositAccountsIcon />} message='Please create your first receiving account.' />
        )}
      </PageBody>

      {hasPagination && (
        <PageFooter>
          <Paginator page={searchParams.page} limit={searchParams.limit} total={totalCount} />
        </PageFooter>
      )}
    </ContentLayout>
  );
};
